import { cn } from "@/lib/utils";
import { STAGE_LABELS, type Stage } from "@ow/shared";

type Tone = "success" | "info" | "warning" | "destructive" | "muted" | "accent";

const TONE_CLASS: Record<Tone, string> = {
  success: "bg-[var(--color-success)]/12 text-[var(--color-success)] border-[var(--color-success)]/30",
  info: "bg-[var(--color-info)]/12 text-[var(--color-info)] border-[var(--color-info)]/30",
  warning: "bg-[var(--color-warning)]/12 text-[var(--color-warning)] border-[var(--color-warning)]/30",
  destructive: "bg-[var(--color-destructive)]/12 text-[var(--color-destructive)] border-[var(--color-destructive)]/30",
  muted: "bg-muted text-muted-foreground border-border",
  accent: "bg-[var(--brand-accent)]/25 text-foreground border-[var(--brand-accent)]",
};

// Distinct hue per candidate status so they don't blur together: placed = green (the terminal win),
// active = blue (live / in-progress), on_hold = amber (paused), rejected = red, withdrawn = grey.
const STATUS_TONE: Record<string, Tone> = {
  active: "info",
  proceed: "success",
  placed: "success",
  on_track: "success",
  on_hold: "warning",
  hold: "warning",
  withdrawn: "muted",
  closed: "muted",
  rejected: "destructive",
  reject: "destructive",
  over_budget: "destructive",
};

const STATUS_LABEL: Record<string, string> = {
  active: "Active",
  on_hold: "On Hold",
  withdrawn: "Withdrawn",
  rejected: "Rejected",
  placed: "Placed",
  closed: "Closed",
  proceed: "Proceed",
  hold: "Hold",
  reject: "Reject",
};

function Pill({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-xs uppercase tracking-wide",
        TONE_CLASS[tone],
      )}
    >
      {children}
    </span>
  );
}

/** Colour-coded status/recommendation pill (Active / On Hold / Rejected / …). */
export function StatusPill({ value }: { value: string }) {
  return <Pill tone={STATUS_TONE[value] ?? "muted"}>{STATUS_LABEL[value] ?? value}</Pill>;
}

/** Stage badge — current/lead stage shown in brand-accent by the pipeline tracker. */
export function StageBadge({ stage, current }: { stage: Stage; current?: boolean }) {
  return <Pill tone={current ? "accent" : "muted"}>{STAGE_LABELS[stage]}</Pill>;
}
